import { clone } from '@jack-henry/frontend-utils/functions';
import {
    CompanyAccountModelDto,
    FilterAccountModelDto,
    FilterBaseModelDto,
} from '@treasury/api/channel';
import { CompanyAccount } from '../company/company-account.entity';
import { DomainEntity } from '../shared';
import { nullFilterBaseModelDto } from './report-list-item.entity';
import { ReportPageId } from './report-results.entity';
import { ReportType, TransactionCode } from './reports.service';

const defaultDto: FilterBaseModelDto = {
    ...nullFilterBaseModelDto,
    accountTypes: 'Checking,Savings',
    type: 'BalanceFilter',
};

const validAccountTypes = ['Checking', 'Savings', 'Loan', 'Checking,Savings'];

export class ReportFilters extends DomainEntity<FilterBaseModelDto> {
    constructor(dto = clone(defaultDto)) {
        if (!validAccountTypes.includes(dto.accountTypes)) {
            dto.accountTypes = 'Checking,Savings';
        }
        super(dto);
        this.dto = dto;
    }

    protected dto: FilterBaseModelDto;

    private _account?: CompanyAccount;

    private _reportAccountItems: { label: string | undefined; value: CompanyAccount }[] = [];

    private _paymentTypes: TransactionCode[] | [] = [];

    private _postedTransactions: boolean | string = false;

    private _transactionDirection: string = 'Both';

    private _transactionType: string = 'Both';

    private _checkNumberOperator: string = 'Specific Check Number';

    private _fromPaymentId?: number;

    private _toPaymentId?: number;

    private _amountOperator?: string = '';

    private _fromAmount?: number;

    private _toAmount?: number;

    public createDefault(): this {
        return new ReportFilters(this.initialDto) as this;
    }

    public get name() {
        return this.dto.name || '';
    }

    public get description() {
        return this.dto.description || '';
    }

    public get reportType() {
        return this.dto.reportType || '';
    }

    public get standardReportTypeId() {
        return this.dto.standardReportTypeId || 0;
    }

    public get isStandardReport() {
        return this.dto.isStandardReport || false;
    }

    public get dateType() {
        return this.dto.dateType || '';
    }

    public set dateType(value) {
        if (value.includes(' to ')) {
            value = value.replace(/ /g, '-');
        }
        this.dto.dateType = value;
    }

    public get fromDate() {
        if (this.dto.updatedDate === this.dto.fromDate) {
            return undefined;
        }
        return this.dto.fromDate || undefined;
    }

    public set fromDate(value) {
        this.dto.fromDate = value;
    }

    public get toDate() {
        if (this.dto.updatedDate === this.dto.toDate) {
            return undefined;
        }
        return this.dto.toDate || undefined;
    }

    public set toDate(value) {
        this.dto.toDate = value;
    }

    public get specificDate() {
        if (this.dto.updatedDate === this.dto.specificDate) {
            return undefined;
        }
        return this.dto.specificDate || undefined;
    }

    public set specificDate(value) {
        this.dto.specificDate = value;
    }

    public get isDateRange() {
        return this.dto.dateType !== 'Current Day' && this.dto.dateType !== 'Prior Day';
    }

    public get priorDays() {
        return this.dto.priorDays || undefined;
    }

    public get sortBy() {
        return this.dto.sortBy || '';
    }

    public set sortBy(value) {
        this.dto.sortBy = value;
    }

    public get isAscendingSort() {
        return this.dto.isAscendingSort || false;
    }

    public set isAscendingSort(value) {
        this.dto.isAscendingSort = value;
    }

    public get owner() {
        return this.dto.owner || undefined;
    }

    public get accountTypes() {
        return this.dto.accountTypes.split(',');
    }

    public set accountTypes(value: string | string[]) {
        this.dto.accountTypes = Array.isArray(value) ? value.join(',') : value;
    }

    // TODO: Remove this once TmSelect supports non-string values
    public get accountTypesString() {
        return this.dto.accountTypes;
    }

    public get accounts() {
        return this.dto.accounts?.map(a => new CompanyAccount(a.account)) ?? [];
    }

    public set accounts(value) {
        this.dto.accounts = value.map(a => ({
            account: a.toDto(),
            id: a.id,
        })) as unknown as FilterAccountModelDto[];
    }

    public get filteredAccounts() {
        return this.accounts.filter(acc => this.accountTypes.includes(acc.type ?? ''));
    }

    public get account() {
        return this._account;
    }

    public set account(value) {
        this._account = value;
    }

    public get typeRequest() {
        switch (this.dto.type) {
            case 'BalanceFilter':
                if (this.isDateRange) {
                    return ReportType.DateRangeBalance;
                }
                if (this.dto.dateType === 'Prior Day') {
                    return ReportType.PriorDayBalance;
                }
                return ReportType.CurrentDayBalance;
            case 'TransactionFilter':
                if (this.isDateRange) {
                    return ReportType.DateRangeTransaction;
                }
                if (this.dto.dateType === 'Prior Day') {
                    return ReportType.PriorDayTransaction;
                }
                return ReportType.CurrentDayTransaction;
            case 'EDIFilter':
                return ReportType.Edi;
            default:
                return '';
        }
    }

    public get pageId() {
        if (this.isDateRange) {
            if (this.dto.reportType === 'Balance') {
                return ReportPageId.DateRangeBalance;
            } else {
                return ReportPageId.DateRangeTransaction;
            }
        } else if (this.dto.dateType === 'Prior Day') {
            if (this.dto.reportType === 'Balance') {
                return ReportPageId.PriorDayBalance;
            } else {
                return ReportPageId.PriorDayTransaction;
            }
        } else if (this.dto.dateType === 'Current Day') {
            if (this.dto.reportType === 'Balance') {
                return ReportPageId.CurrentDayBalance;
            } else {
                return ReportPageId.CurrentDayTransaction;
            }
        } else {
            return ReportPageId.Edi;
        }
    }

    public get paymentTypes() {
        return this._paymentTypes;
    }

    public set paymentTypes(value) {
        this._paymentTypes = value;
    }

    public get checkNumberOperator() {
        return this._checkNumberOperator;
    }

    public set checkNumberOperator(value) {
        this._checkNumberOperator = value;
    }

    public get fromPaymentId() {
        return this._fromPaymentId;
    }

    public set fromPaymentId(value) {
        this._fromPaymentId = value;
    }

    public get toPaymentId() {
        return this._toPaymentId;
    }

    public set toPaymentId(value) {
        this._toPaymentId = value;
    }

    public get amountOperator() {
        return this._amountOperator;
    }

    public set amountOperator(value) {
        this._amountOperator = value;
    }

    public get fromAmount() {
        return this._fromAmount;
    }

    public set fromAmount(value) {
        this._fromAmount = value;
    }

    public get toAmount() {
        return this._toAmount;
    }

    public set toAmount(value) {
        this._toAmount = value;
    }

    public get type() {
        return this.dto.type || '';
    }

    public get postedTransactions() {
        return this._postedTransactions;
    }

    public set postedTransactions(value) {
        this._postedTransactions = value;
    }

    public get transactionDirection() {
        return this._transactionDirection;
    }

    public set transactionDirection(value) {
        this._transactionDirection = value;
    }

    public get transactionType() {
        return this._transactionType;
    }

    public set transactionType(value) {
        this._transactionType = value;
    }

    public get sequence() {
        return this.dto.sequence || undefined;
    }

    public get isFavorite() {
        return this.dto.isFavorite || false;
    }

    public get id() {
        return this.dto.id || 0;
    }

    public get updatedDate() {
        return this.dto.updatedDate || '';
    }

    public get isValid() {
        return this.accounts.length > 0;
    }

    public toDtoMapped() {
        return {
            ...this.dto,
            accounts: this.filteredAccounts?.map(acc => {
                return {
                    ...(acc.toDto() as CompanyAccountModelDto),
                    account: acc.toDto() as CompanyAccountModelDto,
                };
            }),
            account: { ...this.account, account: this.account },
            transactionDirection: this.transactionDirection,
            transactionType: this.transactionType,
            paymentTypes: this.paymentTypes.map(pt => ({ type: pt.name })),
            postedTransactions:
                this.name === 'EDI' ? this.toBoolean(this.postedTransactions) : undefined,
            checkNumberOperator: this.checkNumberOperator,
            fromPaymentId: this.fromPaymentId,
            toPaymentId: this.toPaymentId,
            amountOperator: this.amountOperator,
            fromAmount: this.fromAmount,
            toAmount: this.toAmount,
            page: this.pageId,
        };
    }

    private toBoolean(value: string | boolean) {
        if (typeof value === 'boolean') {
            return value;
        }
        return value === 'true';
    }
}
